import React, { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import CustomInput from "../../components/CustomInput";
import { deepClone, toCamelCase } from "../../../helpers/objectHelper";
import { inputValidator } from "../../helpers/inputValidator.helper";
import { runInAction, toJS } from "mobx";
import Axios from "axios";
import { usePrevious } from "../../helpers/usePrevious";

const Field = observer((props) => {
  const inputTimerRef = useRef(null);
  const { settings, environment, fields, activePersonNo, updateOrder, contentModuleId, pages, validations, checkConditions, contentLayoutModuleId, conditions, current, validate } = props;
  const { fieldId, prefixClassName, type, readonly, required, hidden, validationType, fieldType, minLength, confirmInput, requireUniqueEmail = false, disableChangeLock = false } = settings;

  const orders = toJS(props.orders);

  const field = fields.find((f) => f.fieldId === settings.fieldId);

  const validity = useMemo(() => validations.find((v) => v.id == contentModuleId), [validations]);

  const [isConfirmValid, setIsConfirmValid] = useState(!confirmInput);
  const [isEmailUnique, setIsEmailUnique] = useState(true);

  const fValue = useMemo(() => {
    if (field?.type) {
      const val = toJS(orders)[activePersonNo]?.[toCamelCase(field?.type)]?.[toCamelCase(field.name == "CountryCode" ? "CountryId" : field.name)];
      return val ?? "";
    }
    return "";
  }, [orders, activePersonNo]);

  const [confirmInputValue, setConfirmInputValue] = useState(fValue);
  const previousValue = usePrevious(fValue);

  useEffect(() => {
    if (previousValue !== fValue) {
      if (inputTimerRef.current) {
        clearTimeout(inputTimerRef.current);
        inputTimerRef.current = null;
      }
      inputTimerRef.current = setTimeout(checkRegisteredEmail, 500);
    }
    if (confirmInput) compareConfirmInput();
    validateField();
  }, [confirmInputValue, fValue, activePersonNo, isConfirmValid, isEmailUnique]);

  useEffect(() => {
    setConfirmInputValue(fValue ?? "");
  }, [activePersonNo]);

  const handleValueChange = (value) => {
    let newValue = value;

    if (!field?.type) return;

    let order = orders[activePersonNo];
    let newOrder = deepClone(order);
    let type = newOrder[toCamelCase(field.type)];
    let newType = undefined;
    if (!type) {
      newType = {};
    } else {
      newType = deepClone(type);
    }
    if (field.name == "CountryCode") newType[toCamelCase("CountryId")] = newValue;
    else newType[toCamelCase(field.name)] = newValue;
    newOrder[toCamelCase(field.type)] = newType;
    updateOrder(newOrder);
    checkConditions(contentLayoutModuleId);
  };

  const compareConfirmInput = () => {
    if (fValue.toLowerCase().trim() === confirmInputValue.toLowerCase().trim()) {
      if (!isConfirmValid) setIsConfirmValid(true);
    } else {
      if (isConfirmValid) setIsConfirmValid(false);
    }
  };

  const checkRegisteredEmail = async () => {
    if (requireUniqueEmail && fieldType.toLowerCase() == "email" && fValue.length > 0) {
      try {
        const { data } = await Axios.get(`./api/attendee/emailcheck/${environment.eventId}?email=${encodeURIComponent(fValue.toLowerCase().trim())}`);
        if (data.success) setIsEmailUnique(!data.regExists);
      } catch (error) {
        console.error(error);
      }
    } else {
      setIsEmailUnique(true);
    }
  };

  const validateField = () => {
    let input = fValue;
    let isValid = true;
    let reason = "";
    if (["FIRSTNAME", "LASTNAME", "EMAIL"].includes(fieldType.toUpperCase()) && fValue.length === 0) {
      isValid = false;
      reason = "required";
    } else if (readonly || hidden) {
      isValid = true;
      reason = "";
    } else if (required === true && input.length === 0) {
      isValid = false;
      reason = "required";
    } else if (minLength > 0 && input.length < minLength) {
      isValid = false;
      reason = "minLength";
    } else if (!isConfirmValid) {
      isValid = false;
      reason = "confirmNotMatching";
    } else if (!isEmailUnique) {
      isValid = false;
      reason = "emailNotUnique";
    } else if (validationType !== "none") {
      isValid = inputValidator(input, validationType);
      reason = validationType;
    }

    runInAction(() => {
      if (validity) {
        validity.valid = isValid;
        validity.reason = reason;
      }
    });
  };

  useEffect(() => {
    if (orders[activePersonNo]?.badgeNo) {
      validateField();
    }
  }, [orders, validateField]);

  const isValid = useMemo(() => {
    if (["email", "firstname", "lastname"].includes(fieldType.toLowerCase()) && !disableChangeLock) return true;
    if (fValue.length > 0 && validity?.valid === false) return false;
    else if (fValue.length > 0 && validity?.valid === true) return true;
    else if (validate && validations.find((v) => v.id === contentModuleId)?.valid === false) return false;
    return undefined;
  }, [validity, validate, validations, disableChangeLock, fValue, validateField, contentModuleId, fieldType]);

  const isChangeLocked = useMemo(() => {
    if (["email", "firstname", "lastname"].includes(fieldType.toLowerCase()) && !disableChangeLock && orders?.[0]?.badgeNo) return true;
    return false;
  }, [disableChangeLock, orders, fieldType]);

  if (hidden) return null;
  return (
    <CustomInput
      moduleType={"FIELD"}
      moduleId={contentLayoutModuleId}
      prefixClassName={prefixClassName + ` ${type}`}
      type={fieldType}
      readonly={readonly || isChangeLocked}
      value={fValue}
      required={minLength > 0 || required || ["email", "firstname", "lastname"].includes(fieldType.toLowerCase())}
      lang={environment.lang}
      eventId={environment.eventId}
      showConfirmInput={confirmInput}
      settings={settings}
      confirmInputValue={confirmInputValue}
      confirmInputOnChange={(e) => setConfirmInputValue(e.target.value)}
      valid={isValid}
      onChange={(e) => handleValueChange(e.target ? e.target.value : e)}
      label={fields.find((f) => f.fieldId == fieldId)?.fieldLanguage?.find((fl) => fl.languageCode == environment.lang)?.label || settings.defaultLabel}
      subLabel={fields.find((f) => f.fieldId == fieldId)?.fieldLanguage?.find((fl) => fl.languageCode == environment.lang)?.subLabel}
    />
  );
});

export default Field;
